import { useCallback, useMemo, useRef, useState } from "react"

import { Guid, IApiLink } from "@/types"
import usePagedData from "hooks/usePagedData"
import { FairValueReferenceNumberEvaluationItem, IFairValueEvaluationSort } from "models/FairValueEvaluation"
import FinanceService from "services/FinanceService"
import toastMessages from "stores/toastMessages"
import { ApiLinkEntity, ApiLinkRel } from 'config/constants';
import { IAmountWithCurrencyDto } from "models/AmountWithCurrency"


interface IUseFairValueEvaluationProps {
    bookingPeriod?: string
    filters: {
        userProfitCenterIds: Guid[]
        profitCenterIds?: Guid[]
        assetCategories?: Guid[]
        referenceNumbersSale?: string[]
        missingDepreciationRate?: boolean
        pageSize?: number
        pageNumber?: number
    }
}

export function useFairValueEvaluation({
    bookingPeriod,
    filters
}: IUseFairValueEvaluationProps) {
    const fetchFairValueEvaluationAbortController = useRef<AbortController | null>(null)

    const [sort, setSort] = useState<IFairValueEvaluationSort>({})

    const fetchFairValueEvaluation = useMemo(() => bookingPeriod ? (overWriteReferenceNumberSaleFilter?: string) => {
        if (fetchFairValueEvaluationAbortController.current) {
            fetchFairValueEvaluationAbortController.current.abort()
        }

        fetchFairValueEvaluationAbortController.current = new AbortController()

        return FinanceService.getFairValueEvaluation(bookingPeriod, { ...filters, ...sort, referenceNumberSale: overWriteReferenceNumberSaleFilter ? [overWriteReferenceNumberSaleFilter] : filters.referenceNumbersSale, pageNumber: 1, pageSize: 20 }, fetchFairValueEvaluationAbortController.current.signal)

    } : undefined, [bookingPeriod, filters, sort])

    const {
        items: fairValueEvaluations,
        pagination,
        isLoading,
        loadNextPage,
        hasMore,
        setItems,
        aggregate,
    } = usePagedData<FairValueReferenceNumberEvaluationItem, { links: IApiLink[] }>(
        fetchFairValueEvaluation,
    )
    const noAssets = !fairValueEvaluations?.length && !isLoading

    const checkItem = useCallback(async (link: IApiLink, referenceNumberSale: string) => {
        if (!fetchFairValueEvaluation) return
        await FinanceService.checkFairValueEvaluation(link, filters.userProfitCenterIds)
        const data = await fetchFairValueEvaluation(referenceNumberSale)
        const updatedItem = data.list.find(item => item.referenceNumberSale === referenceNumberSale)
        if (!updatedItem) {
            toastMessages.showSystemError()
            return
        }
        setItems((prev) => (prev?.map(item => item.id === updatedItem.id ? updatedItem : item) ?? []))

    }, [fetchFairValueEvaluation, filters.userProfitCenterIds, setItems])

    const setEstimatedNetSalePrice = useCallback(async ({ link, referenceNumberSale, estimatedNetSalePrice, estimatedSDC, comment }: {
        link: IApiLink | null,
        referenceNumberSale: string,
        estimatedNetSalePrice: IAmountWithCurrencyDto | null,
        estimatedSDC: IAmountWithCurrencyDto | null,
        comment: string | null
    }) => {
        try {
            if (!fetchFairValueEvaluation || !link) return
            await FinanceService.setEstimates(link, filters.userProfitCenterIds, { estimatedNetSalePrice, estimatedSDC, comment })
            const data = await fetchFairValueEvaluation(referenceNumberSale)
            const updatedItem = data.list.find(item => item.referenceNumberSale === referenceNumberSale)
            if (!updatedItem) {
                toastMessages.showSystemError()
                return
            }
            setItems((prev) => (prev?.map(item => item.id === updatedItem.id ? updatedItem : item) ?? []))
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [fetchFairValueEvaluation, filters.userProfitCenterIds, setItems]
    )


    const linkGetBookValues = useMemo(() => {
        return aggregate?.links?.find(link => link.entity === ApiLinkEntity.bookValue && link.rel === ApiLinkRel.all)
    }, [aggregate?.links])

    return {
        fairValueEvaluations,
        noAssets,
        isLoading,
        pagination,
        hasMore,
        loadNextPage,
        checkItem,
        setEstimatedNetSalePrice,
        sort,
        setSort,
        linkGetBookValues,
    }
}
